import { type ApiProblem, call } from '@rental/contracts/client'
import type { VehicleZone } from '@rental/domain'
import { useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import { sendPhoto } from '../photos/send'
import { onResume } from '../resume'
import { reportError } from '../telemetry'
import { type CaptureTransport, createDrainer, type Drainer } from './drainer'
import {
  type CaptureItem,
  type DamageItem,
  type InspectionKey,
  newClientId,
  type PhotoItem,
} from './item'
import { damagesLetGo, linkToDamage, settled, withDependents } from './queue'
import { type CaptureStore, openCaptureStore } from './store'

/** Tabs of the app on this iPad hear when the queue changes, and look again. */
const CHANNEL = 'rental.staff.capture'
/** One sender across tabs (Web Locks), held for one item at a time. */
const LOCK = 'rental.staff.capture.send'
/**
 * Longer than any one item can hold the lock (two asks and two uploads at
 * their time limits): past it, the holder is a tab frozen in the background,
 * and this one takes over. A resend is recorded once (S4a), so the worst is
 * one request too many.
 */
const LOCK_PATIENCE_MS = 8 * 60_000

export interface Captured {
  key: InspectionKey & { plate: string }
  zone: VehicleZone
}

interface CaptureContextValue {
  /** Every item kept on this iPad; null until the store has been read. */
  items: readonly CaptureItem[] | null
  /** False when the browser refused IndexedDB: kept in memory, gone at a reload. */
  durable: boolean
  /** Keeps a photo; throws QuotaExceededError when the iPad is full. */
  capturePhoto: (
    input: Captured & { blob: Blob; damageId?: string; damageItemId?: string },
  ) => Promise<PhotoItem>
  /** Keeps a damage; throws QuotaExceededError when the iPad is full. */
  captureDamage: (input: Captured & { description: string }) => Promise<DamageItem>
  /** Gone from this iPad, unsent; a damage takes its photos with it. Never one on its way. */
  discard: (ids: readonly string[]) => Promise<void>
  /** A refused item gets another chance, now. */
  retry: (id: string) => Promise<void>
  /** Sent items the server's inspection lists now leave the iPad. */
  reconcile: (listed: { photos: ReadonlySet<string>; damages: ReadonlySet<string> }) => void
  /** Calls `listener` when an item reaches the server; returns how to stop. */
  onSent: (listener: (item: CaptureItem) => void) => () => void
}

const CaptureContext = createContext<CaptureContextValue | null>(null)

/** The three requests, each with its time limit's signal. */
const transport: CaptureTransport = {
  postPhoto: (item, damageId, signal) =>
    call(
      api.POST('/v1/staff/bookings/{code}/inspection/photos', {
        params: { path: { code: item.bookingCode } },
        body: {
          stage: item.stage,
          vehicleId: item.vehicleId,
          zone: item.zone,
          clientId: item.id,
          contentType: 'image/jpeg',
          sizeBytes: item.sizeBytes,
          ...(damageId ? { damageId } : {}),
        },
        signal,
      }),
    ),
  putPhoto: (link, blob, signal) => sendPhoto(link, blob, signal),
  postDamage: (item, signal) =>
    call(
      api.POST('/v1/staff/bookings/{code}/inspection/damages', {
        params: { path: { code: item.bookingCode } },
        body: {
          stage: item.stage,
          vehicleId: item.vehicleId,
          clientId: item.id,
          zone: item.zone,
          description: item.description,
        },
        signal,
      }),
    ),
}

/** Web Locks where the browser has them (Safari 15.4+); else one sender in this page. */
function exclusiveSender(): <T>(work: () => Promise<T>) => Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks) {
    let tail: Promise<unknown> = Promise.resolve()
    return (work) => {
      const run = tail.then(work, work)
      tail = run.catch(() => {})
      return run
    }
  }
  return async (work) => {
    let granted = false
    const held = () => {
      granted = true
      return work()
    }
    try {
      return await locks.request(LOCK, { signal: AbortSignal.timeout(LOCK_PATIENCE_MS) }, held)
    } catch (error) {
      if (granted) throw error
      return locks.request(LOCK, { steal: true }, held)
    }
  }
}

/**
 * S4 (OPS-5): the iPad's capture queue for the whole app. It reads the store
 * once, keeps the items for the screens, and runs the drainer: only while the
 * person who took an item is unlocked and the app is in front of them.
 */
export function CaptureProvider({ children }: { children: ReactNode }) {
  const { access, takeOver } = useAccess()
  const queryClient = useQueryClient()
  const [items, setItems] = useState<readonly CaptureItem[] | null>(null)
  const [durable, setDurable] = useState(true)
  const [ready, setReady] = useState(false)
  const store = useRef<CaptureStore | null>(null)
  const drainer = useRef<Drainer | null>(null)
  const channel = useRef<BroadcastChannel | null>(null)
  const listeners = useRef(new Set<(item: CaptureItem) => void>())
  /**
   * The damages this page let go once their inspection listed them, by item,
   * with the server's id: a photo of one, still being converted then, names it
   * when it is kept (queue.ts `linkToDamage`).
   */
  const letGo = useRef(new Map<string, string>())
  const latest = useRef(access)
  latest.current = access
  const takeOverRef = useRef(takeOver)
  takeOverRef.current = takeOver

  const reload = useCallback(async () => {
    if (!store.current) return
    try {
      setItems(await store.current.all())
    } catch (error) {
      reportError(error, { mechanism: 'capture.read', handled: true })
    }
  }, [])

  const changed = useCallback(() => {
    void reload()
    channel.current?.postMessage('changed')
  }, [reload])

  useEffect(() => {
    let closed = false
    const opened = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL)
    channel.current = opened
    void openCaptureStore().then(({ store: kept, durable: onDisk }) => {
      if (closed) return
      store.current = kept
      setDurable(onDisk)
      setReady(true)
      const created = createDrainer({
        store: kept,
        transport,
        sender: () => {
          const now = latest.current
          // Never while turned to the customer, locked, or out of sight (a stale tab).
          if (now.state !== 'unlocked') return null
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return null
          return { userId: now.session.user.id }
        },
        onAccess: (problem: ApiProblem) => takeOverRef.current(problem),
        exclusive: exclusiveSender(),
        now: () => Date.now(),
        setTimer: (run, ms) => window.setTimeout(run, ms),
        clearTimer: (timer) => window.clearTimeout(timer as number),
        changed,
        sent: (item) => {
          void queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey[0] === 'inspection' &&
              String(query.queryKey[1]).toUpperCase() === item.bookingCode,
          })
          for (const listener of listeners.current) listener(item)
        },
        onError: (error) => reportError(error, { mechanism: 'capture.drain', handled: true }),
      })
      drainer.current = created
      void reload()
      created.resume()
    })
    if (opened) {
      opened.onmessage = () => {
        void reload()
        drainer.current?.kick()
      }
    }
    return () => {
      closed = true
      drainer.current?.stop()
      drainer.current = null
      opened?.close()
      channel.current = null
    }
  }, [changed, reload, queryClient])

  /** The queue held by a dropped connection tries again at once: it may be back. */
  const sendSoon = useCallback(() => drainer.current?.resume(), [])

  // The connection is back, the app is back in front, someone unlocked.
  useEffect(() => {
    const online = () => sendSoon()
    window.addEventListener('online', online)
    const stopResume = onResume(sendSoon)
    return () => {
      window.removeEventListener('online', online)
      stopResume()
    }
  }, [sendSoon])
  const unlockedAs = access.state === 'unlocked' ? access.session.user.id : null
  useEffect(() => {
    if (unlockedAs) sendSoon()
  }, [unlockedAs, sendSoon])

  // A1/A2: a revoked device keeps nothing; the queue goes with its credential.
  const revoked = access.state === 'enroll' && access.reason === 'revoked'
  useEffect(() => {
    const kept = store.current
    if (!revoked || !ready || !kept) return
    void kept
      .clear()
      .then(changed)
      .catch((error: unknown) => reportError(error, { mechanism: 'capture.clear', handled: true }))
  }, [revoked, ready, changed])

  const keep = useCallback(
    async <T extends CaptureItem>(item: T): Promise<T> => {
      const kept = store.current
      if (!kept) throw new Error('the capture store is not open yet')
      const holder = { item }
      await kept.mutate((all) => {
        holder.item = linkToDamage(item, all, letGo.current)
        return { put: [holder.item] }
      })
      changed()
      drainer.current?.resume()
      return holder.item
    },
    [changed],
  )

  const base = useCallback((input: Captured) => {
    const now = latest.current
    if (now.state !== 'unlocked') throw new Error('capture while not unlocked')
    const at = Date.now()
    return {
      id: newClientId(),
      bookingCode: input.key.bookingCode.toUpperCase(),
      stage: input.key.stage,
      vehicleId: input.key.vehicleId,
      plate: input.key.plate,
      zone: input.zone,
      capturedBy: {
        userId: now.session.user.id,
        name: now.session.user.displayName,
      },
      deviceId: now.session.device.id,
      capturedAt: at,
      state: 'queued' as const,
      attempts: 0,
      nextAttemptAt: 0,
      problem: null,
      serverId: null,
      sentAt: null,
    }
  }, [])

  const onSent = useCallback((listener: (item: CaptureItem) => void) => {
    listeners.current.add(listener)
    return () => {
      listeners.current.delete(listener)
    }
  }, [])

  const value = useMemo<CaptureContextValue>(
    () => ({
      items,
      durable,
      capturePhoto: async (input) => {
        // Bytes, not the Blob: WebKit's IndexedDB cannot store a Blob (item.ts).
        const bytes = await input.blob.arrayBuffer()
        return keep<PhotoItem>({
          ...base(input),
          kind: 'photo',
          bytes,
          sizeBytes: bytes.byteLength,
          damageId: input.damageId ?? null,
          damageItemId: input.damageItemId ?? null,
        })
      },
      captureDamage: (input) =>
        keep<DamageItem>({
          ...base(input),
          kind: 'damage',
          description: input.description,
        }),
      discard: async (ids) => {
        const kept = store.current
        if (!kept) return
        await kept.mutate((all) => {
          const sending = new Set(all.filter((item) => item.state === 'sending').map((i) => i.id))
          return {
            remove: withDependents(all, ids).filter((id) => !sending.has(id)),
          }
        })
        changed()
      },
      retry: async (id) => {
        const kept = store.current
        if (!kept) return
        await kept.mutate((all) => ({
          put: all
            .filter((item) => item.id === id && item.state === 'refused')
            .map((item) => ({
              ...item,
              state: 'queued' as const,
              attempts: 0,
              nextAttemptAt: 0,
              problem: null,
            })),
        }))
        changed()
        drainer.current?.resume()
      },
      reconcile: (listed) => {
        const kept = store.current
        // Nothing sent here: nothing to let go (and no write for every refetch).
        if (!kept || !items?.some((item) => item.state === 'sent')) return
        void kept
          .mutate((all) => {
            const gone = settled(all, listed, Date.now())
            for (const [itemId, serverId] of damagesLetGo(all, gone)) {
              letGo.current.set(itemId, serverId)
            }
            return { remove: gone }
          })
          .then(() => changed())
          .catch((error: unknown) =>
            reportError(error, {
              mechanism: 'capture.reconcile',
              handled: true,
            }),
          )
      },
      onSent,
    }),
    [items, durable, keep, base, changed, onSent],
  )

  return <CaptureContext.Provider value={value}>{children}</CaptureContext.Provider>
}

export function useCapture(): CaptureContextValue {
  const value = useContext(CaptureContext)
  if (!value) throw new Error('useCapture outside CaptureProvider')
  return value
}
